<template>
  <div class="supply-chain-container">
    <!-- Header with Controls -->
    <div class="supply-chain-header">
      <h2 class="goods-flow-title">Goods flow</h2>
      <div class="filter-controls">
        <div class="filter-toggle" @click="showCompliance = !showCompliance">
          <div class="toggle-switch" :class="{ active: showCompliance }">
            <div class="toggle-slider"></div>
          </div>
          <span class="toggle-label">Compliance</span>
        </div>
        <div class="filter-toggle" @click="showSustainability = !showSustainability">
          <div class="toggle-switch" :class="{ active: showSustainability }">
            <div class="toggle-slider"></div>
          </div>
          <span class="toggle-label">Sustainability</span>
        </div>
        <div class="filter-toggle" @click="showRegulatory = !showRegulatory">
          <div class="toggle-switch" :class="{ active: showRegulatory }">
            <div class="toggle-slider"></div>
          </div>
          <span class="toggle-label">Regulatory checkpoints</span>
        </div>
        <div class="filter-toggle" @click="showRisks = !showRisks">
          <div class="toggle-switch" :class="{ active: showRisks }">
            <div class="toggle-slider"></div>
          </div>
          <span class="toggle-label">Risks</span>
        </div>
      </div>
    </div>

    <!-- Supply Chain Flow -->
    <div class="supply-chain-flow">
      <!-- Origin Farm -->
      <div class="stage-container">
        <div class="stage-header">ORIGIN FARM</div>
        <div class="stage-card farm-card" :class="{ selected: selectedCard === 'farm' }" @click="selectCard('farm')">
          <div class="farm-name">{{ farmDetails?.name || selectedItem?.origin_farm || 'Kookaburra Plains' }}</div>
          <div class="farm-location">{{ farmDetails?.location || 'New England, NSW' }}</div>
          <div class="farm-details">
            <div class="farm-detail">
              <span class="label">PIC:</span>
              <span class="value">{{ selectedItem?.raw_data?.PIC || 'NSWKP1020' }}</span>
            </div>
            <div class="farm-detail">
              <span class="label">Wool Type:</span>
              <span class="value">{{ selectedItem?.type || 'Merino wool fleece' }}</span>
            </div>
            <div class="farm-detail">
              <span class="label">Microns:</span>
              <span class="value">{{ selectedItem?.raw_data?.Micron_um || '18.3' }}μm</span>
            </div>
            <div class="farm-detail">
              <span class="label">Yield:</span>
              <span class="value">{{ selectedItem?.raw_data?.Yield_pct || '72.2' }}%</span>
            </div>
          </div>
          
          <!-- Badges/Labels -->
          <div class="card-badges">
            <div v-if="showCompliance" class="badge compliance-badge">ZQ Certified</div>
            <div v-if="showSustainability" class="badge sustainability-badge">Carbon Neutral</div>
            <div v-if="showRegulatory" class="badge regulatory-badge">AWEX Approved</div>
            <div v-if="showRisks" class="badge risk-badge">Low Risk</div>
          </div>
          
          <div v-if="selectedCard === 'farm'" class="selection-indicator">
            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 17.0001C6.80556 17.0001 6.21528 16.757 5.72917 16.2709C5.24306 15.7848 5 15.1945 5 14.5001C5 14.3473 5.01389 14.19 5.04167 14.0282C5.06944 13.8665 5.11111 13.7183 5.16667 13.5834L3.89583 12.5626C3.70542 12.7034 3.49111 12.8115 3.25292 12.887C3.01486 12.9624 2.76389 13.0001 2.5 13.0001C1.80556 13.0001 1.21528 12.757 0.729167 12.2709C0.243056 11.7848 0 11.1945 0 10.5001C0 9.80564 0.243056 9.21536 0.729167 8.72925C1.21528 8.24314 1.80556 8.00008 2.5 8.00008C2.93056 8.00008 3.32292 8.0973 3.67708 8.29175C4.03125 8.48619 4.32639 8.74314 4.5625 9.06258L6.5 8.47925C6.5 8.20147 6.54861 7.93064 6.64583 7.66675C6.74306 7.40286 6.86806 7.16675 7.02083 6.95842L6.25 5.87508C6.125 5.91675 5.9991 5.94453 5.87229 5.95842C5.74549 5.9723 5.61444 5.97925 5.47917 5.97925C4.78472 5.97925 4.19444 5.73619 3.70833 5.25008C3.22222 4.76397 2.97917 4.17369 2.97917 3.47925C2.97917 2.7848 3.22222 2.19453 3.70833 1.70841C4.19444 1.2223 4.78472 0.979248 5.47917 0.979248C6.17361 0.979248 6.76389 1.2223 7.25 1.70841C7.73611 2.19453 7.97917 2.7848 7.97917 3.47925C7.97917 3.75703 7.93403 4.02786 7.84375 4.29175C7.75347 4.55564 7.625 4.79175 7.45833 5.00008L8.25 6.10425C8.36111 6.06258 8.47854 6.0348 8.60229 6.02092C8.72604 6.00703 8.85861 6.00008 9 6.00008C9.24111 6.00008 9.46806 6.03133 9.68083 6.09383C9.89361 6.15633 10.0972 6.24314 10.2917 6.35425L11.2292 5.52092C11.1597 5.36814 11.1042 5.20515 11.0625 5.03196C11.0208 4.85876 11 4.68147 11 4.50008C11 3.80564 11.2431 3.21536 11.7292 2.72925C12.2153 2.24314 12.8056 2.00008 13.5 2.00008C14.1944 2.00008 14.7847 2.24314 15.2708 2.72925C15.7569 3.21536 16 3.80564 16 4.50008C16 5.19453 15.7569 5.7848 15.2708 6.27092C14.7847 6.75703 14.1944 7.00008 13.5 7.00008C13.2639 7.00008 13.0393 6.96883 12.8263 6.90633C12.6132 6.84383 12.4072 6.75703 12.2083 6.64592L11.2917 7.45842C11.3611 7.61119 11.4132 7.77439 11.4479 7.948C11.4826 8.12161 11.5 8.30564 11.5 8.50008C11.5 9.19453 11.2569 9.7848 10.7708 10.2709C10.2847 10.757 9.69444 11.0001 9 11.0001C8.56944 11.0001 8.17708 10.8994 7.82292 10.698C7.46875 10.4966 7.17361 10.2362 6.9375 9.91675L5 10.5001C5 10.6529 4.98611 10.8056 4.95833 10.9584C4.93056 11.1112 4.88889 11.257 4.83333 11.3959L6.10417 12.4167C6.29806 12.2826 6.5132 12.1796 6.74958 12.1078C6.98597 12.036 7.23611 12.0001 7.5 12.0001C8.19444 12.0001 8.78472 12.2431 9.27083 12.7292C9.75694 13.2154 10 13.8056 10 14.5001C10 15.1945 9.75694 15.7848 9.27083 16.2709C8.78472 16.757 8.19444 17.0001 7.5 17.0001ZM2.5 11.5001C2.78333 11.5001 3.02083 11.4042 3.2125 11.2126C3.40417 11.0209 3.5 10.7834 3.5 10.5001C3.5 10.2167 3.40417 9.97925 3.2125 9.78758C3.02083 9.59591 2.78333 9.50008 2.5 9.50008C2.21667 9.50008 1.97917 9.59591 1.7875 9.78758C1.59583 9.97925 1.5 10.2167 1.5 10.5001C1.5 10.7834 1.59583 11.0209 1.7875 11.2126C1.97917 11.4042 2.21667 11.5001 2.5 11.5001ZM5.5 4.50008C5.78333 4.50008 6.02083 4.40425 6.2125 4.21258C6.40417 4.02092 6.5 3.78341 6.5 3.50008C6.5 3.21675 6.40417 2.97925 6.2125 2.78758C6.02083 2.59591 5.78333 2.50008 5.5 2.50008C5.21667 2.50008 4.97917 2.59591 4.7875 2.78758C4.59583 2.97925 4.5 3.21675 4.5 3.50008C4.5 3.78341 4.59583 4.02092 4.7875 4.21258C4.97917 4.40425 5.21667 4.50008 5.5 4.50008ZM7.5 15.5001C7.78333 15.5001 8.02083 15.4042 8.2125 15.2126C8.40417 15.0209 8.5 14.7834 8.5 14.5001C8.5 14.2167 8.40417 13.9792 8.2125 13.7876C8.02083 13.5959 7.78333 13.5001 7.5 13.5001C7.21667 13.5001 6.97917 13.5959 6.7875 13.7876C6.59583 13.9792 6.5 14.2167 6.5 14.5001C6.5 14.7834 6.59583 15.0209 6.7875 15.2126C6.97917 15.4042 7.21667 15.5001 7.5 15.5001ZM9 9.50008C9.28333 9.50008 9.52083 9.40425 9.7125 9.21258C9.90417 9.02092 10 8.78341 10 8.50008C10 8.21675 9.90417 7.97925 9.7125 7.78758C9.52083 7.59591 9.28333 7.50008 9 7.50008C8.71667 7.50008 8.47917 7.59591 8.2875 7.78758C8.09583 7.97925 8 8.21675 8 8.50008C8 8.78341 8.09583 9.02092 8.2875 9.21258C8.47917 9.40425 8.71667 9.50008 9 9.50008ZM13.5 5.50008C13.7833 5.50008 14.0208 5.40425 14.2125 5.21258C14.4042 5.02092 14.5 4.78341 14.5 4.50008C14.5 4.21675 14.4042 3.97925 14.2125 3.78758C14.0208 3.59591 13.7833 3.50008 13.5 3.50008C13.2167 3.50008 12.9792 3.59591 12.7875 3.78758C12.5958 3.97925 12.5 4.21675 12.5 4.50008C12.5 4.78341 12.5958 5.02092 12.7875 5.21258C12.9792 5.40425 13.2167 5.50008 13.5 5.50008Z" fill="#FF8858"/>
            </svg>
            <span>Place in the center</span>
          </div>
        </div>
      </div>

      <!-- Arrow -->
      <div class="flow-arrow">
        <svg width="40" height="20" viewBox="0 0 40 20" fill="none">
          <path d="M0 10H35M30 5L35 10L30 15" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Authentication -->
      <div class="stage-container">
        <div class="stage-header">AUTHENTICATION</div>
        <div class="stage-card auth-card" :class="{ selected: selectedCard === 'auth' }" @click="selectCard('auth')">
          <div class="auth-name">Authentication</div>
          <div class="cert-number">{{ selectedItem?.awta_certificate || selectedItem?.raw_data?.TestCertID || 'AWTA-KOO-25-001' }}</div>
          <div class="auth-details">
            <div class="auth-detail">
              <span class="label">Test Date:</span>
              <span class="value">{{ formatDate(selectedItem?.raw_data?.SaleDateTime) || '19/4/2025' }}</span>
            </div>
            <div class="auth-detail">
              <span class="label">Lot Number:</span>
              <span class="value">{{ selectedItem?.raw_data?.LotNo || '200' }}</span>
            </div>
            <div class="auth-detail">
              <span class="label">Mulesing:</span>
              <span class="value">{{ selectedItem?.raw_data?.MulesingStatus || 'NM' }}</span>
            </div>
          </div>
          
          <!-- Badges/Labels -->
          <div class="card-badges">
            <div v-if="showCompliance" class="badge compliance-badge">AWTA Verified</div>
            <div v-if="showSustainability" class="badge sustainability-badge">Traceable</div>
            <div v-if="showRegulatory" class="badge regulatory-badge">Export Ready</div>
            <div v-if="showRisks" class="badge risk-badge">Verified</div>
          </div>
          
          <div v-if="selectedCard === 'auth'" class="selection-indicator">
            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 17.0001C6.80556 17.0001 6.21528 16.757 5.72917 16.2709C5.24306 15.7848 5 15.1945 5 14.5001C5 14.3473 5.01389 14.19 5.04167 14.0282C5.06944 13.8665 5.11111 13.7183 5.16667 13.5834L3.89583 12.5626C3.70542 12.7034 3.49111 12.8115 3.25292 12.887C3.01486 12.9624 2.76389 13.0001 2.5 13.0001C1.80556 13.0001 1.21528 12.757 0.729167 12.2709C0.243056 11.7848 0 11.1945 0 10.5001C0 9.80564 0.243056 9.21536 0.729167 8.72925C1.21528 8.24314 1.80556 8.00008 2.5 8.00008C2.93056 8.00008 3.32292 8.0973 3.67708 8.29175C4.03125 8.48619 4.32639 8.74314 4.5625 9.06258L6.5 8.47925C6.5 8.20147 6.54861 7.93064 6.64583 7.66675C6.74306 7.40286 6.86806 7.16675 7.02083 6.95842L6.25 5.87508C6.125 5.91675 5.9991 5.94453 5.87229 5.95842C5.74549 5.9723 5.61444 5.97925 5.47917 5.97925C4.78472 5.97925 4.19444 5.73619 3.70833 5.25008C3.22222 4.76397 2.97917 4.17369 2.97917 3.47925C2.97917 2.7848 3.22222 2.19453 3.70833 1.70841C4.19444 1.2223 4.78472 0.979248 5.47917 0.979248C6.17361 0.979248 6.76389 1.2223 7.25 1.70841C7.73611 2.19453 7.97917 2.7848 7.97917 3.47925C7.97917 3.75703 7.93403 4.02786 7.84375 4.29175C7.75347 4.55564 7.625 4.79175 7.45833 5.00008L8.25 6.10425C8.36111 6.06258 8.47854 6.0348 8.60229 6.02092C8.72604 6.00703 8.85861 6.00008 9 6.00008C9.24111 6.00008 9.46806 6.03133 9.68083 6.09383C9.89361 6.15633 10.0972 6.24314 10.2917 6.35425L11.2292 5.52092C11.1597 5.36814 11.1042 5.20515 11.0625 5.03196C11.0208 4.85876 11 4.68147 11 4.50008C11 3.80564 11.2431 3.21536 11.7292 2.72925C12.2153 2.24314 12.8056 2.00008 13.5 2.00008C14.1944 2.00008 14.7847 2.24314 15.2708 2.72925C15.7569 3.21536 16 3.80564 16 4.50008C16 5.19453 15.7569 5.7848 15.2708 6.27092C14.7847 6.75703 14.1944 7.00008 13.5 7.00008C13.2639 7.00008 13.0393 6.96883 12.8263 6.90633C12.6132 6.84383 12.4072 6.75703 12.2083 6.64592L11.2917 7.45842C11.3611 7.61119 11.4132 7.77439 11.4479 7.948C11.4826 8.12161 11.5 8.30564 11.5 8.50008C11.5 9.19453 11.2569 9.7848 10.7708 10.2709C10.2847 10.757 9.69444 11.0001 9 11.0001C8.56944 11.0001 8.17708 10.8994 7.82292 10.698C7.46875 10.4966 7.17361 10.2362 6.9375 9.91675L5 10.5001C5 10.6529 4.98611 10.8056 4.95833 10.9584C4.93056 11.1112 4.88889 11.257 4.83333 11.3959L6.10417 12.4167C6.29806 12.2826 6.5132 12.1796 6.74958 12.1078C6.98597 12.036 7.23611 12.0001 7.5 12.0001C8.19444 12.0001 8.78472 12.2431 9.27083 12.7292C9.75694 13.2154 10 13.8056 10 14.5001C10 15.1945 9.75694 15.7848 9.27083 16.2709C8.78472 16.757 8.19444 17.0001 7.5 17.0001ZM2.5 11.5001C2.78333 11.5001 3.02083 11.4042 3.2125 11.2126C3.40417 11.0209 3.5 10.7834 3.5 10.5001C3.5 10.2167 3.40417 9.97925 3.2125 9.78758C3.02083 9.59591 2.78333 9.50008 2.5 9.50008C2.21667 9.50008 1.97917 9.59591 1.7875 9.78758C1.59583 9.97925 1.5 10.2167 1.5 10.5001C1.5 10.7834 1.59583 11.0209 1.7875 11.2126C1.97917 11.4042 2.21667 11.5001 2.5 11.5001ZM5.5 4.50008C5.78333 4.50008 6.02083 4.40425 6.2125 4.21258C6.40417 4.02092 6.5 3.78341 6.5 3.50008C6.5 3.21675 6.40417 2.97925 6.2125 2.78758C6.02083 2.59591 5.78333 2.50008 5.5 2.50008C5.21667 2.50008 4.97917 2.59591 4.7875 2.78758C4.59583 2.97925 4.5 3.21675 4.5 3.50008C4.5 3.78341 4.59583 4.02092 4.7875 4.21258C4.97917 4.40425 5.21667 4.50008 5.5 4.50008ZM7.5 15.5001C7.78333 15.5001 8.02083 15.4042 8.2125 15.2126C8.40417 15.0209 8.5 14.7834 8.5 14.5001C8.5 14.2167 8.40417 13.9792 8.2125 13.7876C8.02083 13.5959 7.78333 13.5001 7.5 13.5001C7.21667 13.5001 6.97917 13.5959 6.7875 13.7876C6.59583 13.9792 6.5 14.2167 6.5 14.5001C6.5 14.7834 6.59583 15.0209 6.7875 15.2126C6.97917 15.4042 7.21667 15.5001 7.5 15.5001ZM9 9.50008C9.28333 9.50008 9.52083 9.40425 9.7125 9.21258C9.90417 9.02092 10 8.78341 10 8.50008C10 8.21675 9.90417 7.97925 9.7125 7.78758C9.52083 7.59591 9.28333 7.50008 9 7.50008C8.71667 7.50008 8.47917 7.59591 8.2875 7.78758C8.09583 7.97925 8 8.21675 8 8.50008C8 8.78341 8.09583 9.02092 8.2875 9.21258C8.47917 9.40425 8.71667 9.50008 9 9.50008ZM13.5 5.50008C13.7833 5.50008 14.0208 5.40425 14.2125 5.21258C14.4042 5.02092 14.5 4.78341 14.5 4.50008C14.5 4.21675 14.4042 3.97925 14.2125 3.78758C14.0208 3.59591 13.7833 3.50008 13.5 3.50008C13.2167 3.50008 12.9792 3.59591 12.7875 3.78758C12.5958 3.97925 12.5 4.21675 12.5 4.50008C12.5 4.78341 12.5958 5.02092 12.7875 5.21258C12.9792 5.40425 13.2167 5.50008 13.5 5.50008Z" fill="#FF8858"/>
            </svg>
            <span>Place in the center</span>
          </div>
        </div>
      </div>

      <!-- Arrow -->
      <div class="flow-arrow">
        <svg width="40" height="20" viewBox="0 0 40 20" fill="none">
          <path d="M0 10H35M30 5L35 10L30 15" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Export Consignment -->
      <div class="stage-container">
        <div class="stage-header">EXPORT CONSIGNMENT</div>
        <div class="stage-card export-card" :class="{ selected: selectedCard === 'export' }" @click="selectCard('export')">
          <div class="export-title">{{ selectedItem?.raw_data?.ConsignmentID || selectedItem?.export_consignment || 'CNS-VNM-NM-1' }}</div>
          <div class="export-details">
            <div class="info-row">
              <span class="label">Farm Name:</span>
              <span class="value">{{ farmDetails?.name || selectedResult?.FarmName || 'Kookaburra Plains' }}</span>
            </div>
            <div class="info-row">
              <span class="label">Region:</span>
              <span class="value">{{ farmDetails?.region || selectedResult?.Region || 'New England, NSW' }}</span>
            </div>
            <div class="info-row">
              <span class="label">Owner:</span>
              <span class="value">{{ farmDetails?.owner || selectedResult?.OwnerName || 'John Smith' }}</span>
            </div>
            <div class="info-row">
              <span class="label">PIC:</span>
              <span class="value">{{ farmDetails?.pic || selectedResult?.PIC || 'NLPIC001' }}</span>
            </div>
            <div class="info-row" v-if="selectedResult?.SheepCount">
              <span class="label">Sheep Count:</span>
              <span class="value">{{ selectedResult.SheepCount }}</span>
            </div>
            <div class="info-row" v-if="selectedResult?.ShearingDate">
              <span class="label">Shearing Date:</span>
              <span class="value">{{ formatDate(selectedResult.ShearingDate) }}</span>
            </div>
            <div class="info-row" v-if="selectedResult?.BaleWeight_kg">
              <span class="label">Bale Weight:</span>
              <span class="value">{{ selectedResult.BaleWeight_kg }} kg</span>
            </div>
            <div class="info-row">
              <span class="label">ETD Planned:</span>
              <span class="value">{{ formatDate(selectedResult?.ETD_Planned) || 'TBD' }}</span>
            </div>
            <div class="info-row">
              <span class="label">ETA Actual:</span>
              <span class="value">{{ formatDate(selectedResult?.ETA_Actual) || 'TBD' }}</span>
            </div>
          </div>
          
          <!-- Badges/Labels -->
          <div class="card-badges">
            <div v-if="showCompliance" class="badge compliance-badge">Export Compliant</div>
            <div v-if="showSustainability" class="badge sustainability-badge">Green Shipping</div>
            <div v-if="showRegulatory" class="badge regulatory-badge">Customs Cleared</div>
            <div v-if="showRisks" class="badge risk-badge">Insured</div>
          </div>
          
          <div v-if="selectedCard === 'export'" class="selection-indicator">
            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 17.0001C6.80556 17.0001 6.21528 16.757 5.72917 16.2709C5.24306 15.7848 5 15.1945 5 14.5001C5 14.3473 5.01389 14.19 5.04167 14.0282C5.06944 13.8665 5.11111 13.7183 5.16667 13.5834L3.89583 12.5626C3.70542 12.7034 3.49111 12.8115 3.25292 12.887C3.01486 12.9624 2.76389 13.0001 2.5 13.0001C1.80556 13.0001 1.21528 12.757 0.729167 12.2709C0.243056 11.7848 0 11.1945 0 10.5001C0 9.80564 0.243056 9.21536 0.729167 8.72925C1.21528 8.24314 1.80556 8.00008 2.5 8.00008C2.93056 8.00008 3.32292 8.0973 3.67708 8.29175C4.03125 8.48619 4.32639 8.74314 4.5625 9.06258L6.5 8.47925C6.5 8.20147 6.54861 7.93064 6.64583 7.66675C6.74306 7.40286 6.86806 7.16675 7.02083 6.95842L6.25 5.87508C6.125 5.91675 5.9991 5.94453 5.87229 5.95842C5.74549 5.9723 5.61444 5.97925 5.47917 5.97925C4.78472 5.97925 4.19444 5.73619 3.70833 5.25008C3.22222 4.76397 2.97917 4.17369 2.97917 3.47925C2.97917 2.7848 3.22222 2.19453 3.70833 1.70841C4.19444 1.2223 4.78472 0.979248 5.47917 0.979248C6.17361 0.979248 6.76389 1.2223 7.25 1.70841C7.73611 2.19453 7.97917 2.7848 7.97917 3.47925C7.97917 3.75703 7.93403 4.02786 7.84375 4.29175C7.75347 4.55564 7.625 4.79175 7.45833 5.00008L8.25 6.10425C8.36111 6.06258 8.47854 6.0348 8.60229 6.02092C8.72604 6.00703 8.85861 6.00008 9 6.00008C9.24111 6.00008 9.46806 6.03133 9.68083 6.09383C9.89361 6.15633 10.0972 6.24314 10.2917 6.35425L11.2292 5.52092C11.1597 5.36814 11.1042 5.20515 11.0625 5.03196C11.0208 4.85876 11 4.68147 11 4.50008C11 3.80564 11.2431 3.21536 11.7292 2.72925C12.2153 2.24314 12.8056 2.00008 13.5 2.00008C14.1944 2.00008 14.7847 2.24314 15.2708 2.72925C15.7569 3.21536 16 3.80564 16 4.50008C16 5.19453 15.7569 5.7848 15.2708 6.27092C14.7847 6.75703 14.1944 7.00008 13.5 7.00008C13.2639 7.00008 13.0393 6.96883 12.8263 6.90633C12.6132 6.84383 12.4072 6.75703 12.2083 6.64592L11.2917 7.45842C11.3611 7.61119 11.4132 7.77439 11.4479 7.948C11.4826 8.12161 11.5 8.30564 11.5 8.50008C11.5 9.19453 11.2569 9.7848 10.7708 10.2709C10.2847 10.757 9.69444 11.0001 9 11.0001C8.56944 11.0001 8.17708 10.8994 7.82292 10.698C7.46875 10.4966 7.17361 10.2362 6.9375 9.91675L5 10.5001C5 10.6529 4.98611 10.8056 4.95833 10.9584C4.93056 11.1112 4.88889 11.257 4.83333 11.3959L6.10417 12.4167C6.29806 12.2826 6.5132 12.1796 6.74958 12.1078C6.98597 12.036 7.23611 12.0001 7.5 12.0001C8.19444 12.0001 8.78472 12.2431 9.27083 12.7292C9.75694 13.2154 10 13.8056 10 14.5001C10 15.1945 9.75694 15.7848 9.27083 16.2709C8.78472 16.757 8.19444 17.0001 7.5 17.0001ZM2.5 11.5001C2.78333 11.5001 3.02083 11.4042 3.2125 11.2126C3.40417 11.0209 3.5 10.7834 3.5 10.5001C3.5 10.2167 3.40417 9.97925 3.2125 9.78758C3.02083 9.59591 2.78333 9.50008 2.5 9.50008C2.21667 9.50008 1.97917 9.59591 1.7875 9.78758C1.59583 9.97925 1.5 10.2167 1.5 10.5001C1.5 10.7834 1.59583 11.0209 1.7875 11.2126C1.97917 11.4042 2.21667 11.5001 2.5 11.5001ZM5.5 4.50008C5.78333 4.50008 6.02083 4.40425 6.2125 4.21258C6.40417 4.02092 6.5 3.78341 6.5 3.50008C6.5 3.21675 6.40417 2.97925 6.2125 2.78758C6.02083 2.59591 5.78333 2.50008 5.5 2.50008C5.21667 2.50008 4.97917 2.59591 4.7875 2.78758C4.59583 2.97925 4.5 3.21675 4.5 3.50008C4.5 3.78341 4.59583 4.02092 4.7875 4.21258C4.97917 4.40425 5.21667 4.50008 5.5 4.50008ZM7.5 15.5001C7.78333 15.5001 8.02083 15.4042 8.2125 15.2126C8.40417 15.0209 8.5 14.7834 8.5 14.5001C8.5 14.2167 8.40417 13.9792 8.2125 13.7876C8.02083 13.5959 7.78333 13.5001 7.5 13.5001C7.21667 13.5001 6.97917 13.5959 6.7875 13.7876C6.59583 13.9792 6.5 14.2167 6.5 14.5001C6.5 14.7834 6.59583 15.0209 6.7875 15.2126C6.97917 15.4042 7.21667 15.5001 7.5 15.5001ZM9 9.50008C9.28333 9.50008 9.52083 9.40425 9.7125 9.21258C9.90417 9.02092 10 8.78341 10 8.50008C10 8.21675 9.90417 7.97925 9.7125 7.78758C9.52083 7.59591 9.28333 7.50008 9 7.50008C8.71667 7.50008 8.47917 7.59591 8.2875 7.78758C8.09583 7.97925 8 8.21675 8 8.50008C8 8.78341 8.09583 9.02092 8.2875 9.21258C8.47917 9.40425 8.71667 9.50008 9 9.50008ZM13.5 5.50008C13.7833 5.50008 14.0208 5.40425 14.2125 5.21258C14.4042 5.02092 14.5 4.78341 14.5 4.50008C14.5 4.21675 14.4042 3.97925 14.2125 3.78758C14.0208 3.59591 13.7833 3.50008 13.5 3.50008C13.2167 3.50008 12.9792 3.59591 12.7875 3.78758C12.5958 3.97925 12.5 4.21675 12.5 4.50008C12.5 4.78341 12.5958 5.02092 12.7875 5.21258C12.9792 5.40425 13.2167 5.50008 13.5 5.50008Z" fill="#FF8858"/>
            </svg>
            <span>Place in the center</span>
          </div>
        </div>
      </div>

      <!-- Arrow -->
      <div class="flow-arrow">
        <svg width="40" height="20" viewBox="0 0 40 20" fill="none">
          <path d="M0 10H35M30 5L35 10L30 15" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Manufacturing -->
      <div class="stage-container">
        <div class="stage-header">MANUFACTURING</div>
        <div class="stage-card manufacturing-card" :class="{ selected: selectedCard === 'manufacturing' }" @click="selectCard('manufacturing')">
          <div class="manufacturing-title">{{ factoryDetails?.name || selectedItem?.destination || 'Da Nang Merino Knitwear Ltd.' }}</div>
          <div class="manufacturing-location">{{ factoryDetails?.country || 'Vietnam' }}</div>
          <div class="manufacturing-details">
            <div class="manufacturing-detail">
              <span class="label">Factory ID:</span>
              <span class="value">{{ selectedItem?.raw_data?.FactoryID || 'VN-DN-01' }}</span>
            </div>
            <div class="manufacturing-detail">
              <span class="label">Brand:</span>
              <span class="value">{{ selectedItem?.raw_data?.Brand || 'Icebreaker' }}</span>
            </div>
            <div class="manufacturing-detail">
              <span class="label">Process:</span>
              <span class="risk-badge" v-if="showRisks">Low Risk</span>
            </div>
          </div>
          
          <!-- Badges/Labels -->
          <div class="card-badges">
            <div v-if="showCompliance" class="badge compliance-badge">Fair Trade</div>
            <div v-if="showSustainability" class="badge sustainability-badge">Renewable Energy</div>
            <div v-if="showRegulatory" class="badge regulatory-badge">ISO Certified</div>
            <div v-if="showRisks" class="badge risk-badge">Audited</div>
          </div>
          
          <div v-if="selectedCard === 'manufacturing'" class="selection-indicator">
            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 17.0001C6.80556 17.0001 6.21528 16.757 5.72917 16.2709C5.24306 15.7848 5 15.1945 5 14.5001C5 14.3473 5.01389 14.19 5.04167 14.0282C5.06944 13.8665 5.11111 13.7183 5.16667 13.5834L3.89583 12.5626C3.70542 12.7034 3.49111 12.8115 3.25292 12.887C3.01486 12.9624 2.76389 13.0001 2.5 13.0001C1.80556 13.0001 1.21528 12.757 0.729167 12.2709C0.243056 11.7848 0 11.1945 0 10.5001C0 9.80564 0.243056 9.21536 0.729167 8.72925C1.21528 8.24314 1.80556 8.00008 2.5 8.00008C2.93056 8.00008 3.32292 8.0973 3.67708 8.29175C4.03125 8.48619 4.32639 8.74314 4.5625 9.06258L6.5 8.47925C6.5 8.20147 6.54861 7.93064 6.64583 7.66675C6.74306 7.40286 6.86806 7.16675 7.02083 6.95842L6.25 5.87508C6.125 5.91675 5.9991 5.94453 5.87229 5.95842C5.74549 5.9723 5.61444 5.97925 5.47917 5.97925C4.78472 5.97925 4.19444 5.73619 3.70833 5.25008C3.22222 4.76397 2.97917 4.17369 2.97917 3.47925C2.97917 2.7848 3.22222 2.19453 3.70833 1.70841C4.19444 1.2223 4.78472 0.979248 5.47917 0.979248C6.17361 0.979248 6.76389 1.2223 7.25 1.70841C7.73611 2.19453 7.97917 2.7848 7.97917 3.47925C7.97917 3.75703 7.93403 4.02786 7.84375 4.29175C7.75347 4.55564 7.625 4.79175 7.45833 5.00008L8.25 6.10425C8.36111 6.06258 8.47854 6.0348 8.60229 6.02092C8.72604 6.00703 8.85861 6.00008 9 6.00008C9.24111 6.00008 9.46806 6.03133 9.68083 6.09383C9.89361 6.15633 10.0972 6.24314 10.2917 6.35425L11.2292 5.52092C11.1597 5.36814 11.1042 5.20515 11.0625 5.03196C11.0208 4.85876 11 4.68147 11 4.50008C11 3.80564 11.2431 3.21536 11.7292 2.72925C12.2153 2.24314 12.8056 2.00008 13.5 2.00008C14.1944 2.00008 14.7847 2.24314 15.2708 2.72925C15.7569 3.21536 16 3.80564 16 4.50008C16 5.19453 15.7569 5.7848 15.2708 6.27092C14.7847 6.75703 14.1944 7.00008 13.5 7.00008C13.2639 7.00008 13.0393 6.96883 12.8263 6.90633C12.6132 6.84383 12.4072 6.75703 12.2083 6.64592L11.2917 7.45842C11.3611 7.61119 11.4132 7.77439 11.4479 7.948C11.4826 8.12161 11.5 8.30564 11.5 8.50008C11.5 9.19453 11.2569 9.7848 10.7708 10.2709C10.2847 10.757 9.69444 11.0001 9 11.0001C8.56944 11.0001 8.17708 10.8994 7.82292 10.698C7.46875 10.4966 7.17361 10.2362 6.9375 9.91675L5 10.5001C5 10.6529 4.98611 10.8056 4.95833 10.9584C4.93056 11.1112 4.88889 11.257 4.83333 11.3959L6.10417 12.4167C6.29806 12.2826 6.5132 12.1796 6.74958 12.1078C6.98597 12.036 7.23611 12.0001 7.5 12.0001C8.19444 12.0001 8.78472 12.2431 9.27083 12.7292C9.75694 13.2154 10 13.8056 10 14.5001C10 15.1945 9.75694 15.7848 9.27083 16.2709C8.78472 16.757 8.19444 17.0001 7.5 17.0001ZM2.5 11.5001C2.78333 11.5001 3.02083 11.4042 3.2125 11.2126C3.40417 11.0209 3.5 10.7834 3.5 10.5001C3.5 10.2167 3.40417 9.97925 3.2125 9.78758C3.02083 9.59591 2.78333 9.50008 2.5 9.50008C2.21667 9.50008 1.97917 9.59591 1.7875 9.78758C1.59583 9.97925 1.5 10.2167 1.5 10.5001C1.5 10.7834 1.59583 11.0209 1.7875 11.2126C1.97917 11.4042 2.21667 11.5001 2.5 11.5001ZM5.5 4.50008C5.78333 4.50008 6.02083 4.40425 6.2125 4.21258C6.40417 4.02092 6.5 3.78341 6.5 3.50008C6.5 3.21675 6.40417 2.97925 6.2125 2.78758C6.02083 2.59591 5.78333 2.50008 5.5 2.50008C5.21667 2.50008 4.97917 2.59591 4.7875 2.78758C4.59583 2.97925 4.5 3.21675 4.5 3.50008C4.5 3.78341 4.59583 4.02092 4.7875 4.21258C4.97917 4.40425 5.21667 4.50008 5.5 4.50008ZM7.5 15.5001C7.78333 15.5001 8.02083 15.4042 8.2125 15.2126C8.40417 15.0209 8.5 14.7834 8.5 14.5001C8.5 14.2167 8.40417 13.9792 8.2125 13.7876C8.02083 13.5959 7.78333 13.5001 7.5 13.5001C7.21667 13.5001 6.97917 13.5959 6.7875 13.7876C6.59583 13.9792 6.5 14.2167 6.5 14.5001C6.5 14.7834 6.59583 15.0209 6.7875 15.2126C6.97917 15.4042 7.21667 15.5001 7.5 15.5001ZM9 9.50008C9.28333 9.50008 9.52083 9.40425 9.7125 9.21258C9.90417 9.02092 10 8.78341 10 8.50008C10 8.21675 9.90417 7.97925 9.7125 7.78758C9.52083 7.59591 9.28333 7.50008 9 7.50008C8.71667 7.50008 8.47917 7.59591 8.2875 7.78758C8.09583 7.97925 8 8.21675 8 8.50008C8 8.78341 8.09583 9.02092 8.2875 9.21258C8.47917 9.40425 8.71667 9.50008 9 9.50008ZM13.5 5.50008C13.7833 5.50008 14.0208 5.40425 14.2125 5.21258C14.4042 5.02092 14.5 4.78341 14.5 4.50008C14.5 4.21675 14.4042 3.97925 14.2125 3.78758C14.0208 3.59591 13.7833 3.50008 13.5 3.50008C13.2167 3.50008 12.9792 3.59591 12.7875 3.78758C12.5958 3.97925 12.5 4.21675 12.5 4.50008C12.5 4.78341 12.5958 5.02092 12.7875 5.21258C12.9792 5.40425 13.2167 5.50008 13.5 5.50008Z" fill="#FF8858"/>
            </svg>
            <span>Place in the center</span>
          </div>
        </div>
      </div>

      <!-- Arrow -->
      <div class="flow-arrow">
        <svg width="40" height="20" viewBox="0 0 40 20" fill="none">
          <path d="M0 10H35M30 5L35 10L30 15" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Destination -->
      <div class="stage-container">
        <div class="stage-header">DESTINATION</div>
        <div class="stage-card destination-card" :class="{ selected: selectedCard === 'destination' }" @click="selectCard('destination')">
          <div class="destination-title">Retail Distribution</div>
          <div class="destination-details">
            <div class="destination-detail">
              <span class="label">Brand:</span>
              <span class="value">{{ selectedItem?.raw_data?.Brand || 'Icebreaker' }}</span>
            </div>
            <div class="destination-detail">
              <span class="label">Product:</span>
              <span class="value">Merino Base Layer</span>
            </div>
            <div class="destination-detail">
              <span class="label">Market:</span>
              <span class="value">Global Retail</span>
            </div>
          </div>
          
          <!-- Badges/Labels -->
          <div class="card-badges">
            <div v-if="showCompliance" class="badge compliance-badge">Brand Approved</div>
            <div v-if="showSustainability" class="badge sustainability-badge">Circular Design</div>
            <div v-if="showRegulatory" class="badge regulatory-badge">Consumer Safe</div>
            <div v-if="showRisks" class="badge risk-badge">Quality Assured</div>
          </div>
          
          <div v-if="selectedCard === 'destination'" class="selection-indicator">
            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 17.0001C6.80556 17.0001 6.21528 16.757 5.72917 16.2709C5.24306 15.7848 5 15.1945 5 14.5001C5 14.3473 5.01389 14.19 5.04167 14.0282C5.06944 13.8665 5.11111 13.7183 5.16667 13.5834L3.89583 12.5626C3.70542 12.7034 3.49111 12.8115 3.25292 12.887C3.01486 12.9624 2.76389 13.0001 2.5 13.0001C1.80556 13.0001 1.21528 12.757 0.729167 12.2709C0.243056 11.7848 0 11.1945 0 10.5001C0 9.80564 0.243056 9.21536 0.729167 8.72925C1.21528 8.24314 1.80556 8.00008 2.5 8.00008C2.93056 8.00008 3.32292 8.0973 3.67708 8.29175C4.03125 8.48619 4.32639 8.74314 4.5625 9.06258L6.5 8.47925C6.5 8.20147 6.54861 7.93064 6.64583 7.66675C6.74306 7.40286 6.86806 7.16675 7.02083 6.95842L6.25 5.87508C6.125 5.91675 5.9991 5.94453 5.87229 5.95842C5.74549 5.9723 5.61444 5.97925 5.47917 5.97925C4.78472 5.97925 4.19444 5.73619 3.70833 5.25008C3.22222 4.76397 2.97917 4.17369 2.97917 3.47925C2.97917 2.7848 3.22222 2.19453 3.70833 1.70841C4.19444 1.2223 4.78472 0.979248 5.47917 0.979248C6.17361 0.979248 6.76389 1.2223 7.25 1.70841C7.73611 2.19453 7.97917 2.7848 7.97917 3.47925C7.97917 3.75703 7.93403 4.02786 7.84375 4.29175C7.75347 4.55564 7.625 4.79175 7.45833 5.00008L8.25 6.10425C8.36111 6.06258 8.47854 6.0348 8.60229 6.02092C8.72604 6.00703 8.85861 6.00008 9 6.00008C9.24111 6.00008 9.46806 6.03133 9.68083 6.09383C9.89361 6.15633 10.0972 6.24314 10.2917 6.35425L11.2292 5.52092C11.1597 5.36814 11.1042 5.20515 11.0625 5.03196C11.0208 4.85876 11 4.68147 11 4.50008C11 3.80564 11.2431 3.21536 11.7292 2.72925C12.2153 2.24314 12.8056 2.00008 13.5 2.00008C14.1944 2.00008 14.7847 2.24314 15.2708 2.72925C15.7569 3.21536 16 3.80564 16 4.50008C16 5.19453 15.7569 5.7848 15.2708 6.27092C14.7847 6.75703 14.1944 7.00008 13.5 7.00008C13.2639 7.00008 13.0393 6.96883 12.8263 6.90633C12.6132 6.84383 12.4072 6.75703 12.2083 6.64592L11.2917 7.45842C11.3611 7.61119 11.4132 7.77439 11.4479 7.948C11.4826 8.12161 11.5 8.30564 11.5 8.50008C11.5 9.19453 11.2569 9.7848 10.7708 10.2709C10.2847 10.757 9.69444 11.0001 9 11.0001C8.56944 11.0001 8.17708 10.8994 7.82292 10.698C7.46875 10.4966 7.17361 10.2362 6.9375 9.91675L5 10.5001C5 10.6529 4.98611 10.8056 4.95833 10.9584C4.93056 11.1112 4.88889 11.257 4.83333 11.3959L6.10417 12.4167C6.29806 12.2826 6.5132 12.1796 6.74958 12.1078C6.98597 12.036 7.23611 12.0001 7.5 12.0001C8.19444 12.0001 8.78472 12.2431 9.27083 12.7292C9.75694 13.2154 10 13.8056 10 14.5001C10 15.1945 9.75694 15.7848 9.27083 16.2709C8.78472 16.757 8.19444 17.0001 7.5 17.0001ZM2.5 11.5001C2.78333 11.5001 3.02083 11.4042 3.2125 11.2126C3.40417 11.0209 3.5 10.7834 3.5 10.5001C3.5 10.2167 3.40417 9.97925 3.2125 9.78758C3.02083 9.59591 2.78333 9.50008 2.5 9.50008C2.21667 9.50008 1.97917 9.59591 1.7875 9.78758C1.59583 9.97925 1.5 10.2167 1.5 10.5001C1.5 10.7834 1.59583 11.0209 1.7875 11.2126C1.97917 11.4042 2.21667 11.5001 2.5 11.5001ZM5.5 4.50008C5.78333 4.50008 6.02083 4.40425 6.2125 4.21258C6.40417 4.02092 6.5 3.78341 6.5 3.50008C6.5 3.21675 6.40417 2.97925 6.2125 2.78758C6.02083 2.59591 5.78333 2.50008 5.5 2.50008C5.21667 2.50008 4.97917 2.59591 4.7875 2.78758C4.59583 2.97925 4.5 3.21675 4.5 3.50008C4.5 3.78341 4.59583 4.02092 4.7875 4.21258C4.97917 4.40425 5.21667 4.50008 5.5 4.50008ZM7.5 15.5001C7.78333 15.5001 8.02083 15.4042 8.2125 15.2126C8.40417 15.0209 8.5 14.7834 8.5 14.5001C8.5 14.2167 8.40417 13.9792 8.2125 13.7876C8.02083 13.5959 7.78333 13.5001 7.5 13.5001C7.21667 13.5001 6.97917 13.5959 6.7875 13.7876C6.59583 13.9792 6.5 14.2167 6.5 14.5001C6.5 14.7834 6.59583 15.0209 6.7875 15.2126C6.97917 15.4042 7.21667 15.5001 7.5 15.5001ZM9 9.50008C9.28333 9.50008 9.52083 9.40425 9.7125 9.21258C9.90417 9.02092 10 8.78341 10 8.50008C10 8.21675 9.90417 7.97925 9.7125 7.78758C9.52083 7.59591 9.28333 7.50008 9 7.50008C8.71667 7.50008 8.47917 7.59591 8.2875 7.78758C8.09583 7.97925 8 8.21675 8 8.50008C8 8.78341 8.09583 9.02092 8.2875 9.21258C8.47917 9.40425 8.71667 9.50008 9 9.50008ZM13.5 5.50008C13.7833 5.50008 14.0208 5.40425 14.2125 5.21258C14.4042 5.02092 14.5 4.78341 14.5 4.50008C14.5 4.21675 14.4042 3.97925 14.2125 3.78758C14.0208 3.59591 13.7833 3.50008 13.5 3.50008C13.2167 3.50008 12.9792 3.59591 12.7875 3.78758C12.5958 3.97925 12.5 4.21675 12.5 4.50008C12.5 4.78341 12.5958 5.02092 12.7875 5.21258C12.9792 5.40425 13.2167 5.50008 13.5 5.50008Z" fill="#FF8858"/>
            </svg>
            <span>Place in the center</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    selectedItem: {
      type: Object,
      required: true
    },
    farmDetails: {
      type: Object,
      default: () => ({})
    },
    factoryDetails: {
      type: Object,
      default: () => ({})
    },
    exportDetails: {
      type: Object,
      default: () => ({})
    },
    selectedResult: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedCard: null,
      showCompliance: true,
      showSustainability: false,
      showRegulatory: true,
      showRisks: false
    };
  },
  methods: {
    selectCard(cardType) {
      this.selectedCard = this.selectedCard === cardType ? null : cardType;
    },
    formatDate(dateString) {
      if (!dateString) return null;
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    }
  }
};
</script>

<style scoped>
.supply-chain-container {
  padding: 24px;
  background: rgb(245, 248, 247);
  min-height: 600px;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Header Styles */
.supply-chain-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 0 24px;
}

.goods-flow-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.filter-controls {
  display: flex;
  gap: 16px;
  align-items: center;
}

.filter-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.toggle-switch {
  width: 44px;
  height: 24px;
  background: #e5e7eb;
  border-radius: 12px;
  position: relative;
  transition: background-color 0.2s ease;
}

.toggle-switch.active {
  background: #3A895F;
}

.toggle-slider {
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-switch.active .toggle-slider {
  transform: translateX(20px);
}

.toggle-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

/* Badge Styles */
.card-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
}

.badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.compliance-badge {
  background: #dcfce7;
  color: #166534;
}

.sustainability-badge {
  background: #dbeafe;
  color: #1e40af;
}

.regulatory-badge {
  background: #fef3c7;
  color: #92400e;
}

.risk-badge {
  background: #fee2e2;
  color: #991b1b;
}

/* Selection Indicator Styles */
.selection-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  margin-top: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #FF8858;
}

.selection-indicator svg {
  flex-shrink: 0;
}

.supply-chain-flow {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 20px 24px;
  overflow-x: auto;
  flex: 1;
  min-width: 0;
}

.stage-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 160px;
  max-width: 200px;
}

.stage-header {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
  text-align: center;
}

.stage-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  width: 100%;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.stage-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.stage-card.selected {
  border: 2px solid #FF8858;
  box-shadow: 0 6px 16px rgba(255, 136, 88, 0.2);
}

/* Farm Card */
.farm-card {
  border-left: 4px solid #10b981;
}

.farm-name {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
}

.farm-location {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
}

.farm-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.farm-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.farm-detail .label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.farm-detail .value {
  font-size: 12px;
  color: #111827;
  font-weight: 600;
}

/* Authentication Card */
.auth-card {
  border-left: 4px solid #3b82f6;
}

.auth-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.cert-number {
  font-size: 13px;
  color: #3b82f6;
  font-weight: 600;
  margin-bottom: 16px;
}

.auth-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auth-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.auth-detail .label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.auth-detail .value {
  font-size: 13px;
  color: #111827;
  font-weight: 600;
}

/* Export Card */
.export-card {
  border-left: 4px solid #f59e0b;
}

.export-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
}

.export-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.export-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.export-detail .label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.export-detail .value {
  font-size: 13px;
  color: #111827;
  font-weight: 600;
}

/* Manufacturing Card */
.manufacturing-card {
  border-left: 4px solid #8b5cf6;
}

.manufacturing-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
}

.manufacturing-location {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;
}

.manufacturing-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.manufacturing-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.manufacturing-detail .label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.manufacturing-detail .value {
  font-size: 13px;
  color: #111827;
  font-weight: 600;
}

/* Destination Card */
.destination-card {
  border-left: 4px solid #ef4444;
}

.destination-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
}

.destination-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.destination-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.destination-detail .label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.destination-detail .value {
  font-size: 13px;
  color: #111827;
  font-weight: 600;
}

/* Flow Arrows */
.flow-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  flex-shrink: 0;
  align-self: center;
  margin-top: 40px;
  width: 40px;
}

.flow-arrow svg {
  opacity: 0.6;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .supply-chain-flow {
    gap: 12px;
  }
  
  .stage-container {
    width: 180px;
  }
}

@media (max-width: 1000px) {
  .supply-chain-flow {
    flex-direction: column;
    gap: 20px;
    padding: 16px 12px;
  }
  
  .stage-container {
    width: 100%;
    max-width: 400px;
  }
  
  .flow-arrow {
    transform: rotate(90deg);
    margin: 16px 0;
    margin-top: 16px;
  }
  
  .stage-container {
    min-width: 280px;
    max-width: 100%;
  }
  
  .stage-card {
    padding: 14px;
    min-height: 160px;
  }
}

@media (max-width: 768px) {
  .supply-chain-container {
    padding: 16px;
  }
  
  .stage-container {
    min-width: 280px;
  }
  
  .stage-card {
    padding: 16px;
    min-height: 160px;
  }
}
</style>
